import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import type { CuadreAccount } from "../../../features/sg/cuadre";
import { PLATFORMS } from "../../../features/sg/platforms";
import { StatusBadge } from "../components/cells";
import { PlatformLogo } from "../components/PlatformLogo";
import { Logos, fmt } from "./ui";

interface Props {
  rows: CuadreAccount[];
  onUnits: (id: number) => void;
  onFix: (a: CuadreAccount) => void;
  isDarkMode: boolean;
}

export default function AccountsTable({ rows, onUnits, onFix, isDarkMode }: Props) {
  const { t } = useTranslation("sg");
  const head = isDarkMode ? "text-gray-400 border-dark-border" : "text-gray-500 border-gray-200";
  const row = isDarkMode ? "border-dark-border hover:bg-white/[0.03]" : "border-gray-100 hover:bg-gray-50";
  const strong = isDarkMode ? "text-white" : "text-gray-900";
  const muted = isDarkMode ? "text-gray-500" : "text-gray-400";
  const num = (n: number) => (n ? <span className={`tabular-nums ${strong}`}>{fmt(n)}</span> : <span className={muted}>—</span>);

  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[900px] text-sm">
        <thead>
          <tr className={`border-b text-left text-[11px] uppercase tracking-wider ${head}`}>
            <th className="py-2.5 pr-4 font-medium">{t("cuadre.cols.account")}</th>
            <th className="py-2.5 pr-4 font-medium text-right">{t("cuadre.cols.sgUnits")}</th>
            {PLATFORMS.map((p) => (
              <th key={p} className="py-2.5 pr-4 font-medium text-right">
                <span className="inline-flex" title={p}>
                  <PlatformLogo platform={p} size={16} />
                </span>
              </th>
            ))}
            <th className="py-2.5 pr-4 font-medium text-right">{t("cuadre.platform.none")}</th>
            <th className="py-2.5 pr-4 font-medium">{t("cuadre.cols.reporting")}</th>
            <th className="py-2.5 pr-4 font-medium">{t("cuadre.cols.origin")}</th>
            <th className="py-2.5 w-28" aria-label={t("cuadre.cols.actions")} />
          </tr>
        </thead>
        <tbody>
          {rows.map((a) => {
            const pct = a.units ? Math.round((a.reporting / a.units) * 100) : 0;
            return (
              <tr key={a.id} className={`border-b transition-colors ${row}`}>
                <td className="py-2.5 pr-4 max-w-[280px]">
                  <div className={`truncate font-medium ${strong}`} title={a.name}>
                    {a.name}
                  </div>
                  <div className="mt-0.5 flex flex-wrap gap-1">
                    <StatusBadge label={t(a.billable ? "cuadre.billing.billable" : "cuadre.billing.notBillable")} isDarkMode={isDarkMode} />
                    {a.condition && <StatusBadge label={a.condition} isDarkMode={isDarkMode} />}
                  </div>
                </td>
                <td className="py-2.5 pr-4 text-right font-semibold">{num(a.units)}</td>
                {PLATFORMS.map((p) => (
                  <td key={p} className="py-2.5 pr-4 text-right">
                    {num(a.byPlatform[p])}
                  </td>
                ))}
                <td className="py-2.5 pr-4 text-right">
                  {a.byPlatform.none ? <span className="tabular-nums text-red-500 font-medium">{fmt(a.byPlatform.none)}</span> : <span className={muted}>—</span>}
                </td>
                <td className="py-2.5 pr-4 min-w-[140px]">
                  {a.units ? (
                    <div className="flex items-center gap-2">
                      <div className={`flex-1 h-1.5 rounded-full overflow-hidden ${isDarkMode ? "bg-white/10" : "bg-gray-100"}`}>
                        <div className={`h-full rounded-full ${pct >= 90 ? "bg-emerald-500" : pct >= 60 ? "bg-amber-500" : "bg-red-500"}`} style={{ width: `${pct}%` }} />
                      </div>
                      <span className={`tabular-nums text-xs w-14 text-right ${isDarkMode ? "text-gray-300" : "text-gray-600"}`}>
                        {fmt(a.reporting)}/{fmt(a.units)}
                      </span>
                    </div>
                  ) : (
                    <span className={muted}>—</span>
                  )}
                </td>
                <td className="py-2.5 pr-4">
                  <span className="inline-flex items-center gap-2">
                    <Logos list={a.sgOrigin} />
                    {a.mismatch && (
                      <>
                        <i className="ri-arrow-right-line text-amber-500" aria-label={t("cuadre.mismatchShort")} />
                        <Logos list={a.realOrigin} />
                      </>
                    )}
                  </span>
                </td>
                <td className="py-2.5 text-right whitespace-nowrap">
                  {a.mismatch && (
                    <button
                      type="button"
                      onClick={() => onFix(a)}
                      title={t("origen.fixOne")}
                      aria-label={t("origen.fixOne")}
                      className={`inline-flex w-8 h-8 items-center justify-center rounded-lg text-amber-500 transition-colors ${isDarkMode ? "hover:bg-white/10" : "hover:bg-amber-50"}`}
                    >
                      <i className="ri-git-merge-line" />
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => onUnits(a.id)}
                    title={t("cuadre.actions.viewUnits")}
                    aria-label={t("cuadre.actions.viewUnits")}
                    className={`inline-flex w-8 h-8 items-center justify-center rounded-lg transition-colors ${
                      isDarkMode ? "text-gray-400 hover:bg-white/10 hover:text-white" : "text-gray-500 hover:bg-gray-100 hover:text-gray-900"
                    }`}
                  >
                    <i className="ri-list-unordered" />
                  </button>
                  <Link
                    to={`/sg/users/edit/${a.id}`}
                    title={t("cuadre.actions.editAccount")}
                    aria-label={t("cuadre.actions.editAccount")}
                    className={`inline-flex w-8 h-8 items-center justify-center rounded-lg transition-colors ${
                      isDarkMode ? "text-gray-400 hover:bg-white/10 hover:text-white" : "text-gray-500 hover:bg-gray-100 hover:text-gray-900"
                    }`}
                  >
                    <i className="ri-pencil-line" />
                  </Link>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
